"use client";

import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export type FaqItem = {
  question: string;
  answer: string;
};

type FaqAccordionProps = {
  items: FaqItem[];
};

export function FaqAccordion({ items }: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-3 sm:space-y-4">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const panelId = `faq-panel-${index}`;
        const buttonId = `faq-button-${index}`;

        return (
          <div
            key={item.question}
            className={cn(
              "rounded-2xl sm:rounded-3xl border transition-colors duration-300",
              "bg-white dark:bg-zinc-900/50",
              isOpen
                ? "border-brand-red/20 dark:border-brand-gold/25 shadow-sm"
                : "border-black/5 dark:border-white/5"
            )}
          >
            <button
              id={buttonId}
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-start gap-4 sm:gap-5 px-5 py-5 sm:px-7 sm:py-6 text-left"
            >
              <span
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-display font-bold transition-colors",
                  isOpen
                    ? "bg-brand-red text-white dark:bg-brand-gold dark:text-black"
                    : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
                )}
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="min-w-0 flex-1 pt-1">
                <span
                  className={cn(
                    "block text-base sm:text-lg font-display font-bold leading-snug transition-colors",
                    isOpen
                      ? "text-brand-red dark:text-brand-gold"
                      : "text-black dark:text-white"
                  )}
                >
                  {item.question}
                </span>
              </span>
              <span
                className={cn(
                  "mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300",
                  isOpen
                    ? "bg-brand-red/10 text-brand-red dark:bg-brand-gold/15 dark:text-brand-gold rotate-180"
                    : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500"
                )}
              >
                <CaretDown size={16} weight="bold" />
              </span>
            </button>

            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              aria-hidden={!isOpen}
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              )}
            >
              <div className="overflow-hidden">
                <div className="px-5 pb-5 sm:px-7 sm:pb-7 pl-[3.75rem] sm:pl-[4.75rem] pr-14 sm:pr-16">
                  <p className="text-[15px] sm:text-base leading-relaxed text-zinc-600 dark:text-zinc-300">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
